import React, {
  useRef, useState, useEffect,
} from 'react';
import classNames from 'classnames';
import { useIntl } from 'react-intl';
import './SelectInputStyles.scss';
import { useDispatch } from 'react-redux';
import { Icons } from '../../base/BootstrapComponents/BootstrapComponents';
import Dropdown from '../../base/Dropdown/Dropdown';
import TextInput from '../TextInput/TextInput';
import { errorNotification } from '../../../modules/Notifications/notificationDecorator';
import { getResponseError } from '../../../utils/utils';
import { LOADING_DELAY } from '../../../configs';
import Loader from '../../base/Loader/Loader';

export default function CustomSelectInputWithHelper(props) {
  const {
    inputProps = null,
    className = '',
    fieldState = {
      error: '',
      touched: false,
    },
    controlId = `text-input-${Math.random() * 10000}`,
    renderOption = null,
    addPlaceholder = false,
    useDefaultNull = false,
    placeholder = '',
    multiple = false,
    customToggleContent = null,
    disabled = false,
    inputClassName = '',
    portalClassName = '',
    variant = '',
    filterName = 'search',
    customRenderOptions = null,
    loadAction,
    limit = 20,
    additionalFilter = {},
    optionFormatter = null,
    defaultOptions = [],
    removeSelected = () => {},
    addSelected = () => {},
    onSelect = () => {},
    isLazyLoad = false,
  } = props;
  const {
    error,
    isTouched: touched,
  } = fieldState;
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();
  const [toggleFunction, setToggleFunction] = useState(() => () => {});
  const anchorElement = useRef(null);
  const [options, setOptions] = useState([]);
  const [search, setSearch] = useState('');
  const [timer, setTimer] = useState(null);
  const isError = Boolean(error);
  const [selected, setSelected] = useState([]);
  const [isEndOfList, setIsEndOfList] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const optionsListRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [loadedBlocks, setLoadedBlocks] = useState(0);
  const [loader, setLoader] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(false);
  const blockRefs = useRef({});
  const searchRef = useRef('');

  const defaultRenderOption = item => (!item.name && item.nameId ? formatMessage({ id: item.nameId }) : item.name || '');
  let renderOptionFunction = customRenderOptions || defaultRenderOption;
  if (renderOption && typeof renderOption === 'function') {
    renderOptionFunction = renderOption;
  }

  const value = inputProps && (typeof inputProps.value !== 'undefined') ? inputProps.value : multiple ? [] : null;
  const name = inputProps && (typeof inputProps.name !== 'undefined') ? inputProps.name : '';

  const resetPaginationState = () => {
    if (isLazyLoad) {
      setCurrentPage(1);
      setTotal(0);
      setIsEndOfList(false);
      setOptions([]);
      setIsFirstLoad(true);
      setIsLoading(false);
      setLoadedBlocks(0);
      setIsInitialLoading(false);
      blockRefs.current = {};
    }
  };

  useEffect(() => {
    if (typeof loadAction === 'function') {
      if (value.length) {
        setLoader(true);
        dispatch(loadAction({
          filter: {
            id: Array.isArray(value) ? value : [value],
          },
        })).then(data => {
          setLoader(false);
          setSelected((data?.data || []).map(optionFormatter));
          loadInitialBlocks();
        }).catch(() => {
          setLoader(false);
        });
      } else if (isLazyLoad) {
        loadInitialBlocks();
      } else {
        loadOptions();
      }
    }
  }, []);

  const onChange = item => {
    if (inputProps && inputProps.onChange) {
      let changeValue = item.value;
      if (multiple) {
        const newValue = [...value];
        const index = value.indexOf(item.value);
        if (index > -1) {
          newValue.splice(index, 1);
          removeSelected((item.value));
        } else {
          newValue.push(item.value);
          if (!selected.find(e => e.value === item.value)) {
            setSelected([...selected, item]);
          }
          addSelected(item.value);
        }
        changeValue = newValue;
      }
      inputProps.onChange({ target: { value: changeValue, name: inputProps.name } });
      onSelect(item);
    }
  };

  const loadInitialBlocks = () => {
    if (!isLazyLoad || !loadAction) {
      return;
    }

    setIsInitialLoading(true);
    setIsLoading(true);

    const filter = { [filterName]: search, ...additionalFilter };
    setLoader(true);
    dispatch(loadAction({
      filter,
      page: 1,
      limit: limit * 2,
    }))
      .then(data => {
        setLoader(false);
        if (!data?.data) {
          setIsInitialLoading(false);
          setIsLoading(false);
          return;
        }

        const formattedOptions = (data?.data || []).map(optionFormatter || (item => ({ value: item.id, name: item.number })));

        setOptions(formattedOptions);
        setLoadedBlocks(2);
        setIsFirstLoad(false);
        setCurrentPage(2);
        setIsInitialLoading(false);
        setIsLoading(false);

        if (data?.meta?.total) {
          setTotal(data.meta.total);
        }
      })
      .catch(response => {
        setLoader(false);
        errorNotification('Загрузка данных', getResponseError(response));
        setIsInitialLoading(false);
        setIsLoading(false);
      });
  };

  const loadInitialBlocksWithSearch = searchValue => {
    if (!isLazyLoad || !loadAction) {
      return;
    }

    setIsInitialLoading(true);
    setIsLoading(true);

    const filter = { [filterName]: searchValue, ...additionalFilter };
    setLoader(true);
    dispatch(loadAction({
      filter,
      page: 1,
      limit: limit * 2,
    }))
      .then(data => {
        setLoader(false);
        if (!data?.data) {
          setIsInitialLoading(false);
          setIsLoading(false);
          return;
        }

        const formattedOptions = (data?.data || []).map(optionFormatter || (item => ({ value: item.id, name: item.number })));

        setOptions(formattedOptions);
        setLoadedBlocks(2);
        setIsFirstLoad(false);
        setCurrentPage(2);
        setIsInitialLoading(false);
        setIsLoading(false);

        if (data?.meta?.total) {
          setTotal(data.meta.total);
        }
      })
      .catch(response => {
        setLoader(false);
        errorNotification('Загрузка данных', getResponseError(response));
        setIsInitialLoading(false);
        setIsLoading(false);
      });
  };

  const loadOptions = (value = '') => {
    const defaultOptionFormatter = item => ({ value: item.id, name: item.number });
    const filter = { [filterName]: value, ...additionalFilter };

    const params = { filter, limit };

    setIsLoading(true);
    setLoader(true);
    dispatch(loadAction(params))
      .then(data => {
        setLoader(false);
        if (!data?.data) {
          return;
        }
        const formattedOptions = (data?.data || []).map(optionFormatter || defaultOptionFormatter);
        setOptions(formattedOptions);
        setIsLoading(false);
        if (data?.meta?.total) {
          setTotal(data.meta.total);
        }
      })
      .catch(response => {
        setLoader(false);
        errorNotification('Загрузка данных', getResponseError(response));
        setIsLoading(false);
      });
  };

  const onChangeSearch = event => {
    const { value } = event.target;
    setSearch(value);
    searchRef.current = value;

    if (!loadAction) {
      return;
    }

    if (timer) {
      clearTimeout(timer);
    }
    setTimer(setTimeout(() => {
      const currentSearch = searchRef.current;
      if (isLazyLoad) {
        setCurrentPage(1);
        setIsFirstLoad(true);
        setLoadedBlocks(0);
        setOptions([]);
        blockRefs.current = {};
        loadInitialBlocksWithSearch(currentSearch);
      } else {
        loadOptions(currentSearch);
      }
    }, LOADING_DELAY));
  };

  const loadNextBlock = () => {
    if (!isLazyLoad || isLoading) {
      return;
    }

    const nextPage = loadedBlocks + 1;
    setCurrentPage(nextPage);
    setIsLoading(true);
    setLoader(true);
    dispatch(loadAction({
      filter: { [filterName]: search, ...additionalFilter },
      page: nextPage,
      limit,
    }))
      .then(data => {
        setLoader(false);
        if (!data?.data) {
          return;
        }
        const newOptions = (data?.data || []).map(optionFormatter || (item => ({ value: item.id, name: item.number })));
        setOptions(prev => [...prev, ...newOptions]);
        setLoadedBlocks(prev => prev + 1);
        setIsEndOfList(false);
        setIsLoading(false);
      })
      .catch(response => {
        setLoader(false);
        errorNotification('Загрузка данных', getResponseError(response));
        setIsLoading(false);
      });
  };

  const handleScroll = event => {
    if (!isLazyLoad || isLoading || isInitialLoading) {
      return;
    }

    const element = event.target;
    const { scrollTop } = element;
    const { scrollHeight } = element;
    const { clientHeight } = element;

    const totalBlocks = Math.ceil(total / limit);
    const hasMoreBlocks = loadedBlocks < totalBlocks;

    if (!hasMoreBlocks || isEndOfList) {
      return;
    }

    const blockKeys = Object.keys(blockRefs.current);
    for (let i = 0; i < blockKeys.length; i++) {
      const blockKey = blockKeys[i];
      const blockElement = blockRefs.current[blockKey];
      if (blockElement) {
        const blockRect = blockElement.getBoundingClientRect();
        const containerRect = element.getBoundingClientRect();

        const blockTopRelativeToContainer = blockRect.top - containerRect.top;
        const isBlockTopVisible = blockTopRelativeToContainer >= 0 && blockTopRelativeToContainer < clientHeight;

        if (isBlockTopVisible && parseInt(blockKey, 10) === loadedBlocks && loadedBlocks < totalBlocks) {
          setIsEndOfList(true);
          loadNextBlock();
          break;
        }
      }
    }
  };

  const customSetToggleFunction = toggleFn => {
    const wrappedToggle = () => {
      const newState = !isOpen;
      setIsOpen(newState);

      if (!newState) {
        resetPaginationState();
      } else if (isLazyLoad && options.length === 0 && loadAction) {
        loadInitialBlocks();
      } else if (!isLazyLoad && options.length === 0 && loadAction) {
        loadOptions();
      }

      toggleFn();
    };
    setToggleFunction(() => wrappedToggle);
  };

  let inputValue = '';
  if (!multiple) {
    const inputOption = [...defaultOptions, ...options, ...selected].find(i => i.value === value);
    inputValue = inputOption ? defaultRenderOption(inputOption) : '';
  } else {
    const mergedUnique = [...defaultOptions, ...selected, ...options].reduce((acc, item) => {
      if (!acc.some(el => el.value === item.value)) {
        acc.push(item);
      }
      return acc;
    }, []);
    const selectedItems = mergedUnique.filter(item => value.includes(item.value)).map(defaultRenderOption);
    inputValue = selectedItems.join(', ');
  }
  const isSelectedPlaceholder = value => {
    if ((Array.isArray(value) && !value.length) || value === null) {
      return true;
    }
    if (typeof value === 'number' || typeof value === 'string') {
      return !String(value);
    }
    return false;
  };

  const onReset = event => {
    const v = multiple ? [] : (useDefaultNull ? null : '');
    inputProps.onChange({ target: { value: v, name: inputProps.name } });
    event.preventDefault();
    event.stopPropagation();
  };

  const renderArraySelectedItems = () => {
    const optionValues = options.map(item => item.value);
    return (selected.filter(selectedItem => !optionValues.includes(selectedItem.value)) || []).map((item, index) => (
      <div
        key={index}
        className={classNames('select-option selected')}
        onClick={() => { onChange(item); }}
      >
        {renderOptionFunction(item, index, true)}
      </div>
    ));
  };

  return (
    <div className={classNames('form-control-wrapper', className)}>
      <Dropdown
        usePortal
        disabled={disabled}
        setToggleFunction={customSetToggleFunction}
      >
        <Dropdown.Toggle className="select-toggle">
          {!customToggleContent || typeof customToggleContent !== 'function'
            ? (
              <TextInput
                {...props}
                readOnly
                inputAfter={(
                  <div id={controlId} className="select-arrow-wrapper">
                    <Loader loading={loader} />
                    {inputValue ? <Icons customIcon onClick={onReset} width={18} height={18} icon="close" /> : null}
                    <Icons customIcon className="select-arrow" width={12} height={8} icon="arrow-down" />
                  </div>
                        )}
                className={classNames('custom-select', { 'is-invalid': isError && touched }, inputClassName)}
                fieldState={fieldState}
                containerRef={anchorElement}
                inputProps={{
                  name,
                  onChange: () => {},
                  value: inputValue,
                }}
                variant={variant}
              />
            )
            : customToggleContent(props, value, options, anchorElement, loader, selected)}
        </Dropdown.Toggle>
        <Dropdown.Menu
          useResizeContent={false}
          disableClickToggle={multiple}
          rightPadding={32}
          className="select-options-wrapper with-filter"
          anchorElement={anchorElement}
          classNamePortal={classNames('portal-select-options', portalClassName)}
        >
          <TextInput
            inputProps={{
              onChange: onChangeSearch,
              value: search,
              onClick: e => {
                e.stopPropagation();
                e.preventDefault();
              },
            }}
            placeholder="Поиск"
            className="select-search-input"
            inputAfter={(
              <Icons customIcon className="search" width={16} height={16} icon="search-small" />
                )}
          />
          <div
            className="select-options-list"
            ref={optionsListRef}
            onScroll={handleScroll}
          >
            {addPlaceholder
              ? (
                <div
                  className={classNames('select-option', { selected: isSelectedPlaceholder(value) })}
                  onClick={() => { onChange({ value: multiple ? [] : (useDefaultNull ? null : '') }); }}
                >
                  {renderOptionFunction({ name: placeholder || '' })}
                </div>
              )
              : null}

            {isLazyLoad && options.length > 0 && (
              <div className="blocks-container">
                {renderArraySelectedItems()}
                {Array.from({ length: Math.ceil(options.length / limit) }).map((_, blockIndex) => {
                  const start = blockIndex * limit;
                  const end = Math.min(start + limit, options.length);
                  const blockOptions = options.slice(start, end);
                  const pageNumber = blockIndex + 1;

                  return (
                    <div
                      key={blockIndex}
                      className="page-block"
                      data-page={pageNumber}
                      ref={el => {
                        if (el) {
                          blockRefs.current[pageNumber] = el;
                        }
                      }}
                    >
                      {blockOptions.map((item, key) => {
                        let isSelected = item.value === value;
                        if (multiple) {
                          isSelected = !!value.includes(item.value);
                        }
                        return (
                          <div
                            key={key}
                            className={classNames('select-option', { selected: isSelected })}
                            onClick={() => { onChange(item); }}
                          >
                            {renderOptionFunction(item, key, isSelected)}
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            )}

            {!isLazyLoad && [...options, ...selected].map((item, key) => {
              let isSelected = item.value === value;
              if (multiple) {
                isSelected = !!value.includes(item.value);
              }
              return (
                <div
                  key={key}
                  className={classNames('select-option', { selected: isSelected })}
                  onClick={() => { onChange(item); }}
                >
                  {renderOptionFunction(item, key, isSelected)}
                </div>
              );
            })}
          </div>
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
}
